import { useMutation, useQuery } from "@tanstack/react-query";
import { message } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useState } from "react";
import { AiOutlineEdit } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import AntTable from "../../../components/AntTable/AntTable";
import HeaderTableSuperAdmin from "../../../components/admin/common/HeaderTableSuperAdmin";
import useBreadcrumb from "../../../hooks/useBreadcrumb";
import {
  UpdateBulkStatus,
  deleteBulkSalonAdmin,
  getLocationsAdmin,
} from "../../../network/admin";
import queryClient from "../../../services/react-query/queryClient";

type Props = {};
const actionItems = [
  {
    color: "#C43535",
    text: "Delete Salon",
    title: "Confirm Deletion",
    description: "Are you sure you want to delete this salon?",
    status: "delete",
  },
  {
    color: "#479821",
    text: "Accept Salon",
    title: "Confirm Acceptance",
    description: "Accept this salon?",
    status: "active",
  },
  {
    color: "#75757E",
    text: "Reject Salon",
    title: "Confirm Rejection",
    description: "Reject this salon?",
    status: "rejected",
  },
  {
    color: "#75757E",
    text: "Block Salon",
    title: "Confirm Block",
    description: "Block this salon?",
    status: "blocked",
  },
];

const SalonManagementOverview = (props: Props) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState({
    title: "",
    para: "",
    status: "",
    color: "",
  });
  const [searchHeader, setSearchHeader] = useState();
  const [isFilter, setIsFilter] = useState({});
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const { data, isLoading } = useQuery(
    ["AdminLocations", isFilter, searchHeader],
    () => getLocationsAdmin({ ...isFilter, search: searchHeader })
  );

  const statusColor = (status: string) => {
    switch (status) {
      case "active":
      case "Active":
        return "#479821";
      case "Cancelled ":
      case "cancelled ":
      case "blocked":
      case "Blocked":
        return "#C43535";
      case "pending":
      case "Pending":
        return "#CF953D";
      case "Upcoming":
      case "upcoming":
        return "#00086D";
      default:
        return "#B0B0C0"; // Default color if status doesn't match any case
    }
  };
  const columns: ColumnsType<any> = [
    {
      title: (
        <div className={`text-[#9999A7] text-sm font-normal`}>Salon name</div>
      ),
      dataIndex: "first_name",
      render: (text: any, record) => (
        <div className={`text-color-primary text-sm font-medium `}>
          {record?.name}
        </div>
      ),
    },
    {
      title: <div className="text-[#9999A7] text-sm font-normal">Location</div>,
      dataIndex: "phone",
      // responsive: ["lg"],
      render: (text: any, record) => {
        console.log(record);

        return (
          <div className="text-sm font-medium text-color-primary" dir="ltr">
            {record?.business_location?.Address
              ? record?.business_location?.Address
              : `${record?.business_location?.City || ""} ${
                  record?.business_location?.District || ""
                }`}
          </div>
        );
      },
    },
    {
      title: (
        <div className="text-[#9999A7] text-sm font-normal">
          Number of staff
        </div>
      ),
      dataIndex: "display_all_booking",

      render: (text: any, record) => (
        <div className="text-[#9999A7] text-sm font-medium">
          <span className="mx-1 font-semibold text-color-primary">
            {record?.team_members_count}
          </span>
        </div>
      ),
    },
    {
      title: (
        <div className="text-[#9999A7] text-sm font-normal">
          Date of joining
        </div>
      ),
      dataIndex: "totalSales",

      render: (text: any, record) => (
        <div className="text-sm font-medium text-color-primary">
          {dayjs(record?.created_at).format("YYYY-MM-DD")}
        </div>
      ),
    },
    {
      title: <div className="text-[#9999A7] text-sm font-normal">Status</div>,
      dataIndex: "created_at",
      // responsive: ["lg"],
      render: (text: any, record) => (
        <div
          className={`text-[${statusColor(
            record?.status
          )}] text-sm font-medium flex items-center w-full justify-between`}
        >
          <span className="w-full capitalize">{record?.status}</span>
        </div>
      ),
    },
    {
      title: <div></div>,
      dataIndex: "edit",
      // responsive: ["lg"],
      render: (text: any, record) => (
        <Link
          className="w-fit hover:bg-[#EEF0FF] p-2 rounded-full cursor-pointer"
          to={`${record?.id}`}
        >
          <AiOutlineEdit />
        </Link>
      ),
    },
  ];
  const showModalConfirm = (
    title: string,
    para: string,
    status: string,
    color: string
  ) => {
    setConfirmMessage({ title, para, status, color });
    setIsModalConfirmOpen(true);
  };
  const closeModalConfirm = () => {
    setIsModalConfirmOpen(false);
  };
  const breadcrumb = useBreadcrumb([], 1);
  const {
    mutate: updateSalonStatus,
    isLoading: isSubmitting,
    error: addSalonError,
  } = useMutation(UpdateBulkStatus, {
    onSuccess: (res: any) => {
      if (res?.code === "ERR_BAD_REQUEST") {
        message.error(res?.response?.data?.message);
      } else {
        message.success("Status Salons Updated Successfully");
        queryClient.invalidateQueries(["AdminLocations"]);
        closeModalConfirm();
      }
    },
  });
  const { mutate: deleteSalonStatus, isLoading: isDeleteing } = useMutation(
    deleteBulkSalonAdmin,
    {
      onSuccess: (res: any) => {
        if (res?.code === "ERR_BAD_REQUEST" || "ERR_BAD_RESPONSE") {
          message.error(res?.response?.data?.message);
        } else {
          console.log(res);
          message.success("Salon Deleted Successfully");
          queryClient.invalidateQueries(["AdminLocations"]);
          navigate("/admin/salon-management");
          closeModalConfirm();
        }
      },
    }
  );
  return (
    <>
      {breadcrumb}
      <div className="bg-[#EEF0FF] mt-20">
        <AntTable
          tableColumns={columns}
          tableHeader={
            <div className="flex items-center justify-end">
              <HeaderTableSuperAdmin
                searchHeader={searchHeader}
                setSearchHeader={setSearchHeader}
                selectedRowKeys={selectedRowKeys}
                labelButton="New Salon"
                hasModal={false}
                actionItems={actionItems}
                filterType={{ status: true, dateAdded: true, salonType: true }}
                setIsFilter={setIsFilter}
                isDropDown={true}
                modalId={1}
                requestStatus={updateSalonStatus}
                requestDelete={deleteSalonStatus}
                confirmMessage={confirmMessage}
                setConfirmMessage={setConfirmMessage}
                isModalConfirmOpen={isModalConfirmOpen}
                closeModalConfirm={closeModalConfirm}
                showModalConfirm={showModalConfirm}
                placeholderSearch={"Search salons"}
              />
            </div>
          }
          tableData={data?.data || []}
          isLoading={isLoading}
          expandRowByClick={false}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          setPage={setPage}
          page={page}
          total={data?.meta?.total}
        />
      </div>
    </>
  );
};

export default SalonManagementOverview;
